import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface UserProfile {
  full_name: string;
  email: string;
  phone: string;
}

export const useUserProfile = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchProfile = async (userId: string, fallbackEmail = "") => {
      setLoading(true);
      try {
        const { data } = await supabase
          .from("profiles")
          .select("full_name, email, phone")
          .eq("user_id", userId)
          .maybeSingle();

        if (!isMounted) return;

        setProfile({
          full_name: data?.full_name || "",
          email: data?.email || fallbackEmail,
          phone: data?.phone || "",
        });
      } catch {
        if (!isMounted) return;
        setProfile(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    const init = async () => {
      try {
        const { data, error } = await supabase.auth.getUser();
        if (!isMounted) return;

        if (error || !data.user) {
          setProfile(null);
          setLoading(false);
          return;
        }

        await fetchProfile(data.user.id, data.user.email || "");
      } catch {
        if (!isMounted) return;
        setProfile(null);
        setLoading(false);
      }
    };

    void init();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!isMounted) return;

      if (!session?.user) {
        setProfile(null);
        setLoading(false);
        return;
      }

      void fetchProfile(session.user.id, session.user.email || "");
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  return { profile, loading };
};
